import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  Button,
  Chip,
  Heading,
  SearchField,
  SpinningLoader,
  useFormField,
} from '@frontend/design-system';
import { useLocationSupportQueries } from '../../queries/support/queries';
import type { LocationServiceLocation, LocationServiceLocationItem } from '../../types/location.types';
import { getLocationStatus, getLocationStatusLabel } from '../../utils/location-status';
import { StatusDot } from '../atoms/status-dot';
import { LocationInfoCard } from './location-info-card';

export type LocationDetailsTrayProps = {
  onClose: () => void;
  onPinLocation: (location: LocationServiceLocationItem, isPinned: boolean) => void;
  isPinned: (locationId: string) => boolean;
  onChangeLocation: (location: LocationServiceLocationItem) => void;
  location: LocationServiceLocation | null;
  childLocations: LocationServiceLocation[];
};

const styles = {
  tray: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxHeight: '100%',
    height: '100%',
    background: theme.colors.white,
    padding: theme.spacing(3),
    overflow: 'hidden',
  }),
  head: css({
    display: 'flex',
    gap: theme.spacing(2),
    padding: `0 ${theme.spacing(3)}`,
    paddingBottom: theme.spacing(2),
    margin: `0 -${theme.spacing(2)}`,
    borderBottom: `1px solid ${theme.colors.neutral10}`,
    alignItems: 'center',

    '.location-details__close-btn': {
      marginLeft: 'auto',
    },
  }),
  details: css({
    margin: `0 -${theme.spacing(2)}`,
    borderBottom: `1px solid ${theme.colors.neutral10}`,
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  }),
  loadingWrapper: css({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  }),
  accountName: css({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  }),
  accountNameHeading: css({
    flex: 1,
  }),
  accountNamePin: css({
    shrink: 0,

    '.details__close-btn': {
      height: '40px',
      width: '40px',
      padding: theme.spacing(1),
    },
  }),
  parentType: css({
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  statusText: css({
    fontWeight: theme.font.weight.bold,
  }),
  statusGrid: css({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'space-around',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    label: {
      color: theme.colors.neutral50,
      display: 'block',
      marginBottom: '4px',
    },
  }),
  statusRow: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  }),
  locations: css({
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    overflow: 'auto',
  }),
  search: {},
};

export const LocationDetailsTray = ({
  location,
  childLocations,
  onClose,
  isPinned,
  onPinLocation,
  onChangeLocation,
}: LocationDetailsTrayProps) => {
  const { mappedCount, isLoadingAll, hasDepartmentsEnabled, hasSmartphonesEnabled } = useLocationSupportQueries(
    location?.locationId || '',
    location?.parentId || ''
  );

  const searchField = useFormField({ type: 'text' });

  const searchedLocations = useMemo(() => {
    const matched: LocationServiceLocation[] = [];

    const locationMatchesSearch = (l: LocationServiceLocation) => {
      return (
        l.name?.toLowerCase().includes(searchField.value.toLowerCase()) ||
        l.slug?.toLowerCase().includes(searchField.value.toLowerCase())
      );
    };

    if (location && locationMatchesSearch(location)) {
      matched.push(location);
    }

    for (const childLocation of childLocations) {
      if (locationMatchesSearch(childLocation)) {
        matched.push(childLocation);
      }
    }

    return matched;
  }, [searchField.value, location, childLocations]);

  const pinned = useMemo(() => {
    return isPinned(location?.locationId || '');
  }, [isPinned, location]);

  const status = useMemo(() => {
    return getLocationStatus({
      locationId: location?.locationId || '',
      phoneTenantId: location?.phoneTenantId || '',
      parentId: location?.parentId || '',
      childrenLength: childLocations.length,
    });
  }, [location, childLocations]);

  const statusLabel = useMemo(() => {
    return getLocationStatusLabel(status);
  }, [status]);

  return (
    <div className='location-details__tray' css={styles.tray}>
      <div className='location-details__head' css={styles.head}>
        <div>
          <Heading level={1}>Location Information</Heading>
        </div>
        <div className='location-details__close-btn'>
          <Button iconName='x' size='large' variant='secondary' onClick={onClose} />
        </div>
      </div>
      {(!location || isLoadingAll) && (
        <div css={styles.loadingWrapper}>
          <SpinningLoader size='large' />
        </div>
      )}
      {!!location && !isLoadingAll && (
        <>
          <div className='location-details__details' css={styles.details}>
            <div className='details__account-name' css={styles.accountName}>
              <div className='details__account-name-heading' css={styles.accountNameHeading}>
                <Heading level={2}>{location.name || location.slug || '(Unamed account)'}</Heading>
              </div>
              <div className='details__pin-action' css={styles.accountNamePin}>
                <Button
                  variant='secondary'
                  className='details__close-btn'
                  destructive={pinned}
                  onClick={() => onPinLocation({ ...location, locationId: location.locationId || '' }, !pinned)}
                >
                  <Icon name='pin' />
                </Button>
              </div>
            </div>
            {childLocations.length > 0 && (
              <>
                <div className='location-details__parent-type' css={styles.parentType}>
                  <div>
                    <Chip.Location variant='warningDark'>
                      {childLocations.length} {childLocations.length === 1 ? 'Location' : 'Locations'}
                    </Chip.Location>
                  </div>
                  <div className='location-details__status' css={styles.statusText}>
                    {statusLabel}
                  </div>
                </div>
                <div className='location-details__statuses' css={styles.statusGrid}>
                  <div>
                    <label>Multi Setup</label>
                    <div>
                      {mappedCount}/{childLocations.length} Mapped
                    </div>
                  </div>
                  <div>
                    <label>Departments</label>
                    <div>
                      {hasDepartmentsEnabled ? (
                        <>
                          <div className='status__row' css={styles.statusRow}>
                            <div>
                              <StatusDot.Success />
                            </div>
                            <div>Enabled</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className='status__row' css={styles.statusRow}>
                            <div>
                              <StatusDot.Neutral />
                            </div>
                            <div>Disabled</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div>
                    <label>Softphones</label>

                    <div>
                      {hasSmartphonesEnabled ? (
                        <>
                          <div className='status__row' css={styles.statusRow}>
                            <div>
                              <StatusDot.Success />
                            </div>
                            <div>Enabled</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className='status__row' css={styles.statusRow}>
                            <div>
                              <StatusDot.Neutral />
                            </div>
                            <div>Disabled</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {childLocations.length > 0 && (
            <div className='location-details__search' css={styles.search}>
              <SearchField placeholder='Search Locations' name='search-locations' {...searchField} />
            </div>
          )}

          <div className='location-details__locations' css={styles.locations}>
            {searchField.value.length ? (
              <>
                {searchedLocations.map((l) => (
                  <LocationInfoCard
                    isCurrentLocation={location.locationId === l.locationId}
                    onChangeLocation={onChangeLocation}
                    key={l.locationId}
                    location={l}
                  />
                ))}

                {searchedLocations.length === 0 && (
                  <BannerNotification status='info'>No locations matches search</BannerNotification>
                )}
              </>
            ) : (
              <>
                <LocationInfoCard isCurrentLocation={true} onChangeLocation={onChangeLocation} location={location} />

                {childLocations.map((childLocation) => (
                  <LocationInfoCard
                    isCurrentLocation={location.locationId === childLocation.locationId}
                    onChangeLocation={onChangeLocation}
                    key={childLocation.locationId}
                    location={childLocation}
                  />
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
